/*
 * YourXI Bespoke XI: match details (per-player annotations + match-facts footer)
 * --------------------------------------------------------------------------
 * window.YXIMatch
 *
 * PLAYER ANNOTATIONS
 *   mountPlayerEditor(el, {value, onChange(value)}) -> {update(value), getValue(), destroy()}
 *   renderPlayerAnnotations(value, {x, nameBottom, nameSize, style, maxWidth, maxBottom, after}) -> SVG string
 *   layoutPlayerAnnotations(value, opts) -> {svg, height, lines}   (height = space used below nameBottom)
 *   captainSuffix(surname, value) -> 'RATCLIFFE (C)' when value.captain, else surname
 *   normalizePlayer(value)
 *
 *   player value = {
 *     goals:   [{min:'52', type:''|'pen'|'og'}]   up to 6, minutes 1-130 or '90+3'
 *     subOn:   ''|'60'      came on (green up arrow)
 *     subOff:  ''|'75'      went off (red down arrow)
 *     motm:    false        man of the match (gold star)
 *     card:    ''|'yellow'|'red',  cardMin: ''|'34'
 *     captain: false        prints as "(C)" after the surname, like the original prints
 *   }
 *
 *   Measured off the original prints (Everton 1985 GRAY 57', Newcastle 2025 ISAK, KRAFTH sub arrow),
 *   all relative to the surname font size (ls):
 *     ball diameter 0.57 ls, minute Montserrat ExtraBold 0.58 ls, ball-to-minute gap 0.19 ls,
 *     minute baseline 0.85 ls below the surname baseline, ball centred on the minute caps.
 *     Sub arrow: 1.1 x minute cap height tall, 0.82 x as wide, green #6FC933 (sampled), gap 0.57 cap.
 *     Minutes use the closing prime: 57’ (the fixed prints moved to this from ‘52).
 *   Coordinates: x is the name's centre, nameBottom is the surname BASELINE. In the wizard slot <g>
 *   (origin at the shirt anchor, scale p.s) that is x 0, nameBottom = nameTop + (first ? 30 : 10),
 *   nameSize = the fitted surname size (ls). If a nickname line sits under the surname, pass
 *   nameBottom = nickname baseline and after:'nickname' (tighter first offset).
 *
 * MATCH FOOTER (4th footer mode, 'match')
 *   mountFooterEditor(el, {value, onChange(value)}) -> {update, getValue, destroy}
 *   renderMatchFooter(value, {x, y, width, height, style}) -> SVG string ('' when empty)
 *   mountFooterCombined(el, {value, onChange}) -> one editor with None / Message / Achievement / Match
 *   renderFooter(value, opts) -> dispatches to renderMatchFooter or YXIFooter.renderSVG
 *   normalizeFooter(value)
 *
 *   footer value = { mode:'match', home, homeScore, awayScore, away, aet:false, pens:'',
 *                    competition, date:'2025-05-18' (or free text), venue, attendance, referee }
 *
 *   Typography from the Newcastle 2025 Carabao Cup print (900-wide units):
 *     score line  Montserrat Bold 14       "LIVERPOOL 1 - 2 NEWCASTLE UNITED"
 *     after extra time / pens  Bold 8 (0.57 x, as the COV and JUVE fixes)
 *     competition + date  Bold 10.8, line pitch 12.95, ordinal as superscript (16TH)
 *     venue / ATTENDANCE: / REFEREE:  Regular 8.3, line pitch 10.4
 *     baseline gaps: score -> competition 21, date -> venue 19.1
 *
 * INTEGRATION NOTES (wizard/index.html, not edited here)
 *   1. <link rel="stylesheet" href="../modules/matchdetails.css"> and
 *      <script src="../modules/matchdetails.js"></script> after footer.js.
 *   2. State: store a per-slot object, e.g. S.slots[i].match = {...player value} (and subs too).
 *      Captain: keep S.captain as the source of truth. Pass value.captain = (S.captain === i)
 *      into the editor and on change set S.captain = v.captain ? i : (S.captain === i ? null : S.captain).
 *   3. Player sheet: add <div id="matchEd"></div> and
 *      YXIMatch.mountPlayerEditor(matchEd, {value, onChange: v => {x.match = v; save(); refreshAll();}}).
 *   4. slotInnerSVG: replace the round C badge with the print convention,
 *      last = YXIMatch.captainSuffix(last, {captain: S.captain === i}), refit with fitSize, then append
 *      YXIMatch.renderPlayerAnnotations(x.match, {x:0, nameBottom: nameTop + (first?30:10), nameSize: ls,
 *      style: P.getStyle(S.pitchStyle, tint)}) inside <g class="names">. Use layoutPlayerAnnotations(..).height
 *      to grow the .ring/.hit rects (196 -> 196 + height) when a slot has annotations.
 *   5. Footer: S.right gains 'match'. Either mount YXIMatch.mountFooterCombined(el, {value, onChange})
 *      in place of YXIFooter.mount (it hosts the footer.js editor for the first three modes and
 *      hides footer.js's own 3-way switch), and in renderPrint call
 *      YXIMatch.renderFooter(value, {x:545, y:1028, width:300, height:164, style}).
 *      footerValue() should carry the match fields: {..., mode:S.right, match:S.matchFacts}.
 *      Combined value = footer.js value + {match:{...footer value}}; mode lives at the top level.
 *   6. packState: append S.matchFacts and the per-slot match objects (they are small and flat).
 *   7. Sub minutes in the SUBSTITUTES list on the left: YXIMatch.subMarkSVG(minute, {x, baseline, size, style})
 *      draws the green arrow + minute exactly like "KRAFTH ↑ 90’"; place it after the surname tspan.
 */
(function (root) {
  'use strict';

  var FONT = 'Montserrat';
  var GREEN = '#6FC933', DOWN = '#E0322B', YELLOW = '#F7C925', RED = '#D7263D', GOLD = '#D9B44A';
  var MAX_GOALS = 6;
  var uid = 0;

  /* ---------------- helpers ---------------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function r1(n) { return Math.round(n * 100) / 100; }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function clean(s) { return String(s == null ? '' : s).replace(/[–—]/g, '-').replace(/\s+/g, ' '); }
  function lum(hex) {
    var h = String(hex || '#ffffff').replace('#', '');
    if (h.length === 3) h = h.replace(/./g, '$&$&');
    var c = [0, 2, 4].map(function (i) { var v = parseInt(h.substr(i, 2), 16) / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }
  var FALLBACK_STYLES = {
    classic: { paper: '#FFFFFF', textPrimary: '#111111', textSecondary: '#62625F', halo: '#FFFFFF' },
    night: { paper: '#141414', textPrimary: '#F5F3EE', textSecondary: '#A9A7A1', halo: '#141414' }
  };
  function resolveStyle(style) {
    var st;
    if (style && typeof style === 'object') st = style;
    else if (root.YourXIPitches && root.YourXIPitches.getStyle) st = root.YourXIPitches.getStyle(style || 'classic');
    else st = FALLBACK_STYLES[style] || FALLBACK_STYLES.classic;
    var paper = st.paper || st.bg || '#FFFFFF';
    return { ink: st.textPrimary || st.ink || '#111111', soft: st.textSecondary || st.muted || '#62625F', halo: st.halo || paper, paper: paper, dark: lum(paper) < 0.2 };
  }

  var ctx = null;
  function measure(t, size, weight, tracking) {
    if (!ctx && typeof document !== 'undefined') { try { ctx = document.createElement('canvas').getContext('2d'); } catch (e) { ctx = null; } }
    var w;
    if (ctx) { ctx.font = weight + ' ' + size + 'px ' + FONT + ', sans-serif'; w = ctx.measureText(t).width; }
    else w = t.length * size * (weight >= 700 ? 0.68 : 0.62);
    return w + Math.max(0, t.length - 1) * (tracking || 0);
  }

  /* minutes: '52', '90+3'. Returns '' when not valid. */
  function normMin(m) {
    var s = String(m == null ? '' : m).replace(/[’'′\s]/g, '').replace(/^(\d+)\+$/, '$1');
    var x = s.match(/^(\d{1,3})(?:\+(\d{1,2}))?$/);
    if (!x) return '';
    var a = +x[1]; if (a < 1 || a > 130) return '';
    return x[2] ? a + '+' + (+x[2]) : String(a);
  }
  function minKey(m) { var p = String(m).split('+'); return +p[0] + (p[1] ? +p[1] / 100 : 0); }
  function fmtMin(m) { return m + '’'; }

  /* ---------------- player value ---------------- */
  var PLAYER_DEFAULT = { goals: [], subOn: '', subOff: '', motm: false, card: '', cardMin: '', captain: false };
  function normalizePlayer(v) {
    v = v || {};
    var goals = (Array.isArray(v.goals) ? v.goals : []).map(function (g) {
      if (typeof g !== 'object' || g === null) g = { min: g };
      var min = normMin(g.min);
      return min ? { min: min, type: g.type === 'pen' || g.type === 'og' ? g.type : '' } : null;
    }).filter(Boolean).slice(0, MAX_GOALS);
    goals.sort(function (a, b) { return minKey(a.min) - minKey(b.min); });
    return {
      goals: goals,
      subOn: normMin(v.subOn), subOff: normMin(v.subOff),
      motm: !!v.motm,
      card: v.card === 'yellow' || v.card === 'red' ? v.card : '',
      cardMin: v.card === 'yellow' || v.card === 'red' ? normMin(v.cardMin) : '',
      captain: !!v.captain
    };
  }
  function isEmptyPlayer(v) { v = normalizePlayer(v); return !v.goals.length && !v.subOn && !v.subOff && !v.motm && !v.card; }
  function captainSuffix(surname, v) { return v && v.captain ? String(surname || '') + ' (C)' : String(surname || ''); }

  /* ---------------- glyphs (centred on cx, cy) ---------------- */
  function pentagon(cx, cy, r, rot) {
    var p = [];
    for (var k = 0; k < 5; k++) { var a = (rot - 90 + 72 * k) * Math.PI / 180; p.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]); }
    return p;
  }
  function polyD(p) { return 'M' + p.map(function (q) { return r1(q[0]) + ' ' + r1(q[1]); }).join(' L') + ' Z'; }
  // classic black and white ball, as on the prints: outline, centre pentagon, five rim patches, seams
  function ballSVG(cx, cy, d, S) {
    var R = d / 2, sw = Math.max(0.6, d * 0.075), id = 'yxdb' + (++uid);
    var ink = '#111111', out = '';
    var c = pentagon(cx, cy, R * 0.36, 0), seams = '', patches = '';
    for (var k = 0; k < 5; k++) {
      var a = (-90 + 72 * k) * Math.PI / 180;
      var inner = [cx + R * 0.62 * Math.cos(a), cy + R * 0.62 * Math.sin(a)];
      seams += 'M' + r1(c[k][0]) + ' ' + r1(c[k][1]) + ' L' + r1(inner[0]) + ' ' + r1(inner[1]) + ' ';
      var pc = [cx + R * 0.98 * Math.cos(a), cy + R * 0.98 * Math.sin(a)];
      var pp = pentagon(pc[0], pc[1], R * 0.37, 72 * k + 36);
      patches += polyD(pp) + ' ';
      // rim seams between neighbouring patches
      var b = (-90 + 72 * k + 36) * Math.PI / 180;
      seams += 'M' + r1(pp[2][0]) + ' ' + r1(pp[2][1]) + ' L' + r1(cx + R * 1.05 * Math.cos(b)) + ' ' + r1(cy + R * 1.05 * Math.sin(b)) + ' ';
    }
    out += '<circle cx="' + r1(cx) + '" cy="' + r1(cy) + '" r="' + r1(R + sw * 1.4) + '" fill="' + S.halo + '"/>';
    out += '<clipPath id="' + id + '"><circle cx="' + r1(cx) + '" cy="' + r1(cy) + '" r="' + r1(R) + '"/></clipPath>';
    out += '<circle cx="' + r1(cx) + '" cy="' + r1(cy) + '" r="' + r1(R) + '" fill="#FFFFFF"/>';
    out += '<g clip-path="url(#' + id + ')"><path d="' + patches + '" fill="' + ink + '"/><path d="' + seams + '" fill="none" stroke="' + ink + '" stroke-width="' + r1(sw * 0.8) + '" stroke-linecap="round"/></g>';
    out += '<path d="' + polyD(c) + '" fill="' + ink + '"/>';
    out += '<circle cx="' + r1(cx) + '" cy="' + r1(cy) + '" r="' + r1(R - sw / 2) + '" fill="none" stroke="' + ink + '" stroke-width="' + r1(sw) + '"/>';
    if (S.dark) out += '<circle cx="' + r1(cx) + '" cy="' + r1(cy) + '" r="' + r1(R) + '" fill="none" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="' + r1(sw * 0.5) + '"/>';
    return out;
  }
  // block arrow like the KRAFTH ↑ 90’ sub mark: head 46% of the height, shaft 42% of the width
  function arrowSVG(cx, cy, w, h, up, S) {
    var t = cy - h / 2, b = cy + h / 2, hh = h * 0.46, sw = w * 0.42;
    var p = up
      ? [[cx, t], [cx + w / 2, t + hh], [cx + sw / 2, t + hh], [cx + sw / 2, b], [cx - sw / 2, b], [cx - sw / 2, t + hh], [cx - w / 2, t + hh]]
      : [[cx, b], [cx + w / 2, b - hh], [cx + sw / 2, b - hh], [cx + sw / 2, t], [cx - sw / 2, t], [cx - sw / 2, b - hh], [cx - w / 2, b - hh]];
    var d = polyD(p);
    return '<path d="' + d + '" fill="' + S.halo + '" stroke="' + S.halo + '" stroke-width="' + r1(w * 0.3) + '" stroke-linejoin="round"/>' +
      '<path d="' + d + '" fill="' + (up ? GREEN : DOWN) + '"/>';
  }
  function starSVG(cx, cy, d, S) {
    var R = d / 2, r = R * 0.42, p = [];
    for (var k = 0; k < 10; k++) { var a = (-90 + 36 * k) * Math.PI / 180, rr = k % 2 ? r : R; p.push([cx + rr * Math.cos(a), cy + rr * Math.sin(a) + R * 0.08]); }
    var dd = polyD(p), sw = Math.max(0.5, d * 0.05);
    return '<path d="' + dd + '" fill="' + S.halo + '" stroke="' + S.halo + '" stroke-width="' + r1(d * 0.22) + '" stroke-linejoin="round"/>' +
      '<path d="' + dd + '" fill="' + GOLD + '" stroke="' + (S.dark ? '#F5F3EE' : '#111111') + '" stroke-width="' + r1(sw) + '" stroke-linejoin="round"/>';
  }
  function cardSVG(cx, cy, h, colour, S) {
    var w = h * 0.7, sw = Math.max(0.5, h * 0.05);
    var rect = 'x="' + r1(cx - w / 2) + '" y="' + r1(cy - h / 2) + '" width="' + r1(w) + '" height="' + r1(h) + '"';
    return '<g transform="rotate(8 ' + r1(cx) + ' ' + r1(cy) + ')"><rect ' + rect + ' fill="' + S.halo + '" stroke="' + S.halo + '" stroke-width="' + r1(h * 0.24) + '"/>' +
      '<rect ' + rect + ' fill="' + colour + '" stroke="' + (S.dark ? '#F5F3EE' : '#111111') + '" stroke-width="' + r1(sw) + '"/></g>';
  }
  function textSVG(x, base, t, size, S, anchor) {
    return '<text x="' + r1(x) + '" y="' + r1(base) + '" text-anchor="' + (anchor || 'start') + '" font-family="' + FONT + ', sans-serif" font-size="' + r1(size) +
      '" font-weight="800" fill="' + S.ink + '" stroke="' + S.halo + '" stroke-width="' + r1(size * 0.34) + '" stroke-linejoin="round" paint-order="stroke">' + esc(t) + '</text>';
  }

  /* ---------------- player annotations ---------------- */
  function items(v, ls, S) {
    var ms = ls * 0.58, cap = ms * 0.7, gap = ls * 0.19, ball = ls * 0.57, out = [];
    function textItem(icon, iw, t) { // icon then text, returns {w, draw}
      var tw = t ? measure(t, ms, 800) : 0, w = iw + (t ? gap + tw : 0);
      return { w: w, draw: function (x0, base) { var cy = base - cap / 2; return icon(x0 + iw / 2, cy) + (t ? textSVG(x0 + iw + gap, base, t, ms, S) : ''); } };
    }
    if (v.goals.length) {
      var t = v.goals.map(function (g) { return fmtMin(g.min) + (g.type ? ' ' + g.type.toUpperCase() : ''); }).join(', ');
      out.push(textItem(function (cx, cy) { return ballSVG(cx, cy, ball, S); }, ball, t));
    }
    var aw = cap * 0.9, ah = cap * 1.1, agap = cap * 0.57;
    function subItem(m, up) {
      var t = fmtMin(m), tw = measure(t, ms, 800), w = aw + agap + tw;
      return { w: w, draw: function (x0, base) { return arrowSVG(x0 + aw / 2, base - cap / 2, aw, ah, up, S) + textSVG(x0 + aw + agap, base, t, ms, S); } };
    }
    if (v.subOn) out.push(subItem(v.subOn, true));
    if (v.subOff) out.push(subItem(v.subOff, false));
    if (v.card) {
      var ch = cap * 1.25, cw = ch * 0.75;
      out.push(textItem(function (cx, cy) { return cardSVG(cx, cy, ch, v.card === 'red' ? RED : YELLOW, S); }, cw, v.cardMin ? fmtMin(v.cardMin) : ''));
    }
    if (v.motm) { var sd = ball * 1.12; out.push(textItem(function (cx, cy) { return starSVG(cx, cy, sd, S); }, sd, '')); }
    return out;
  }

  function layoutPlayerAnnotations(value, opts) {
    opts = opts || {};
    var v = normalizePlayer(value), S = resolveStyle(opts.style);
    var ls = +opts.nameSize || 30, X = opts.x != null ? +opts.x : 0;
    var nb = opts.nameBottom != null ? +opts.nameBottom : (opts.y != null ? +opts.y : 0);
    var maxW = +opts.maxWidth || 190, ls0 = ls;
    var maxBottom = opts.maxBottom != null ? +opts.maxBottom : Infinity;
    var list, G, lines, lw, first, pitch;
    // opts.maxBottom: the stats must end above the shirt below. Shrink step by step until they fit (min 60%).
    for (var k = 1; ; k -= 0.08) {
      ls = ls0 * Math.max(k, 0.6);
      list = items(v, ls, S);
      if (!list.length) return { svg: '', height: 0, lines: 0 };
      G = ls * 0.42; lines = [[]]; lw = [0];
      list.forEach(function (it) {
        var L = lines.length - 1, nw = lw[L] + (lines[L].length ? G : 0) + it.w;
        if (lines[L].length && nw > maxW) { lines.push([it]); lw.push(it.w); } else { lines[L].push(it); lw[L] = nw; }
      });
      first = opts.after === 'nickname' ? ls * 0.72 : ls * 0.85; pitch = ls * 0.74;
      if (nb + first + (lines.length - 1) * pitch + ls * 0.12 <= maxBottom || k <= 0.6) break;
    }
    var svg = '';
    lines.forEach(function (row, i) {
      var base = nb + first + i * pitch, x = X - lw[i] / 2;
      row.forEach(function (it, k) { if (k) x += G; svg += it.draw(x, base); x += it.w; });
    });
    var height = first + (lines.length - 1) * pitch + ls * 0.12;
    return { svg: '<g class="yxd-ann">' + svg + '</g>', height: height, lines: lines.length };
  }
  function renderPlayerAnnotations(value, opts) { return layoutPlayerAnnotations(value, opts).svg; }

  /* subs list mark: arrow + minute after a surname, sized to that line (size = font size of the line) */
  function subMarkSVG(minute, opts) {
    opts = opts || {};
    var m = normMin(minute); if (!m) return '';
    var S = resolveStyle(opts.style), size = +opts.size || 12, cap = size * 0.7;
    var aw = cap * 0.9, ah = cap * 1.1, gap = cap * 0.57, x = +opts.x || 0, base = +opts.baseline || 0;
    var up = opts.off ? false : true;
    return arrowSVG(x + gap + aw / 2, base - cap / 2, aw, ah, up, S) + textSVG(x + gap * 2 + aw, base, fmtMin(m), size, S);
  }
  function subMarkWidth(minute, size) { var cap = size * 0.7; return cap * 0.57 * 2 + cap * 0.9 + measure(fmtMin(normMin(minute)), size, 800); }

  /* ---------------- player editor ---------------- */
  function mountPlayerEditor(el, opts) {
    opts = opts || {};
    var state = normalizePlayer(opts.value || PLAYER_DEFAULT);
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var open = opts.open != null ? !!opts.open : !isEmptyPlayer(state);
    var id = 'yxd' + (++uid), dead = false, gType = '';
    function emit() { if (!dead) onChange(clone(state)); }
    function summary() {
      var b = [];
      if (state.captain) b.push('Captain');
      if (state.goals.length) b.push(state.goals.length + (state.goals.length > 1 ? ' goals' : ' goal'));
      if (state.subOn) b.push('On ' + fmtMin(state.subOn));
      if (state.subOff) b.push('Off ' + fmtMin(state.subOff));
      if (state.card) b.push(state.card === 'red' ? 'Red card' : 'Yellow card');
      if (state.motm) b.push('MOTM');
      return b.length ? b.join(' · ') : 'Optional';
    }
    function sw(key, on, label) {
      return '<div class="yxd-toggle"><span id="' + id + key + '">' + label + '</span><button type="button" class="yxd-switch" role="switch" aria-labelledby="' + id + key + '" aria-checked="' + on + '" data-sw="' + key + '"></button></div>';
    }
    function render() {
      var a = document.activeElement, f = a && el.contains(a) && a.dataset ? a.dataset.f : null;
      var goals = state.goals.map(function (g, i) {
        return '<li class="yxd-goal"><span class="yxd-ball" aria-hidden="true"></span><b>' + esc(fmtMin(g.min)) + (g.type ? ' ' + g.type.toUpperCase() : '') +
          '</b><button type="button" class="yxd-x" data-del="' + i + '" aria-label="Remove goal at ' + esc(g.min) + ' minutes">&times;</button></li>';
      }).join('');
      el.innerHTML = '<div class="yxd">' +
        '<button type="button" class="yxd-head" aria-expanded="' + open + '" aria-controls="' + id + '-b" data-toggle="1"><span class="yxd-title">Match details</span>' +
        '<span class="yxd-sum">' + esc(summary()) + '</span><span class="yxd-chev" aria-hidden="true"></span></button>' +
        '<div class="yxd-body" id="' + id + '-b"' + (open ? '' : ' hidden') + '>' +
        sw('captain', state.captain, 'Captain <span class="yxd-opt">prints (C) after the name</span>') +
        '<div class="yxd-field"><span class="yxd-label" id="' + id + '-gl">Goals</span>' +
        (goals ? '<ul class="yxd-goals" aria-labelledby="' + id + '-gl">' + goals + '</ul>' : '') +
        (state.goals.length < MAX_GOALS ?
          '<div class="yxd-add"><label class="yxd-min"><span class="sr-only yxd-sr">Goal minute</span><input class="yxd-input" inputmode="numeric" maxlength="6" placeholder="Min" data-f="gmin" aria-label="Goal minute"><i>’</i></label>' +
          '<div class="yxd-mini" role="radiogroup" aria-label="Goal type">' + [['', 'Goal'], ['pen', 'Pen'], ['og', 'OG']].map(function (o) {
            return '<button type="button" role="radio" data-gt="' + o[0] + '" aria-checked="' + (gType === o[0]) + '">' + o[1] + '</button>';
          }).join('') + '</div><button type="button" class="yxd-btn" data-addgoal="1">+ Goal</button></div>' : '<p class="yxd-hint">Six goals is the most we can print.</p>') +
        '</div>' +
        '<div class="yxd-field"><span class="yxd-label">Substitution</span><div class="yxd-subs">' +
        '<label class="yxd-sub"><span class="yxd-arrow up" aria-hidden="true"></span><span>Came on</span><input class="yxd-input" inputmode="numeric" maxlength="6" placeholder="Min" data-f="subOn" value="' + esc(state.subOn) + '" aria-label="Came on, minute"></label>' +
        '<label class="yxd-sub"><span class="yxd-arrow down" aria-hidden="true"></span><span>Went off</span><input class="yxd-input" inputmode="numeric" maxlength="6" placeholder="Min" data-f="subOff" value="' + esc(state.subOff) + '" aria-label="Went off, minute"></label>' +
        '</div></div>' +
        '<div class="yxd-field"><span class="yxd-label" id="' + id + '-cl">Card</span><div class="yxd-cardrow"><div class="yxd-chips" role="radiogroup" aria-labelledby="' + id + '-cl">' +
        [['', 'None'], ['yellow', 'Yellow'], ['red', 'Red']].map(function (o) {
          return '<button type="button" role="radio" data-card="' + o[0] + '" aria-checked="' + (state.card === o[0]) + '">' + (o[0] ? '<i class="yxd-cd yxd-cd-' + o[0] + '"></i>' : '') + o[1] + '</button>';
        }).join('') + '</div>' +
        (state.card ? '<label class="yxd-min"><input class="yxd-input" inputmode="numeric" maxlength="6" placeholder="Min" data-f="cardMin" value="' + esc(state.cardMin) + '" aria-label="Card minute"><i>’</i></label>' : '') +
        '</div></div>' +
        sw('motm', state.motm, 'Man of the match <span class="yxd-opt">gold star</span>') +
        '</div></div>';
      if (f) { var n = el.querySelector('[data-f="' + f + '"]'); if (n) { n.focus(); try { var L = n.value.length; n.setSelectionRange(L, L); } catch (e) {} } }
    }
    function addGoal() {
      var inp = el.querySelector('[data-f="gmin"]'); if (!inp) return;
      var m = normMin(inp.value);
      if (!m) { inp.classList.add('is-bad'); inp.focus(); return; }
      state.goals.push({ min: m, type: gType });
      state = normalizePlayer(state); gType = '';
      render(); emit();
      var n = el.querySelector('[data-f="gmin"]'); if (n) n.focus();
    }
    function onClick(e) {
      var b = e.target.closest && e.target.closest('button'); if (!b || !el.contains(b)) return;
      if (b.dataset.toggle) { open = !open; render(); var h = el.querySelector('.yxd-head'); if (h) h.focus(); return; }
      if (b.dataset.sw) { state[b.dataset.sw] = !state[b.dataset.sw]; }
      else if (b.dataset.del != null) state.goals.splice(+b.dataset.del, 1);
      else if (b.dataset.gt != null) { gType = b.dataset.gt; var keep = el.querySelector('[data-f="gmin"]'), kv = keep ? keep.value : ''; render(); var k2 = el.querySelector('[data-f="gmin"]'); if (k2) k2.value = kv; return; }
      else if (b.dataset.addgoal) return addGoal();
      else if (b.dataset.card != null) { state.card = b.dataset.card; if (!state.card) state.cardMin = ''; }
      else return;
      render(); emit();
      var sel = b.dataset.sw ? '[data-sw="' + b.dataset.sw + '"]' : b.dataset.card != null ? '[data-card="' + b.dataset.card + '"]' : null;
      var again = sel && el.querySelector(sel); if (again) again.focus();
    }
    function onInput(e) {
      var t = e.target, f = t.dataset && t.dataset.f; if (!f) return;
      t.classList.remove('is-bad');
      var v = t.value.replace(/[^\d+]/g, '').slice(0, 6); if (v !== t.value) t.value = v;
      if (f === 'gmin') return;
      var m = normMin(v);
      if (v && !m) { t.classList.add('is-bad'); return; }
      if (state[f] === m) return;
      state[f] = m; emit();
      var s = el.querySelector('.yxd-sum'); if (s) s.textContent = summary();
    }
    function onKey(e) {
      if (e.key === 'Enter' && e.target.dataset && e.target.dataset.f === 'gmin') { e.preventDefault(); addGoal(); }
      var b = e.target; if (!b.getAttribute || b.getAttribute('role') !== 'radio') return;
      var d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!d) return;
      var list = [].slice.call(b.parentNode.querySelectorAll('[role="radio"]')), n = list[(list.indexOf(b) + d + list.length) % list.length];
      e.preventDefault(); n.click(); n.focus();
    }
    el.addEventListener('click', onClick); el.addEventListener('input', onInput); el.addEventListener('keydown', onKey);
    render();
    return {
      update: function (value) { var n = normalizePlayer(value); if (JSON.stringify(n) === JSON.stringify(state)) return; state = n; render(); },
      getValue: function () { return clone(state); },
      destroy: function () { dead = true; el.removeEventListener('click', onClick); el.removeEventListener('input', onInput); el.removeEventListener('keydown', onKey); el.innerHTML = ''; }
    };
  }

  /* ---------------- match footer ---------------- */
  var FLIM = { team: 20, score: 2, pens: 9, competition: 34, venue: 42, attendance: 7, referee: 26 };
  var FOOTER_DEFAULT = { mode: 'match', home: '', homeScore: '', awayScore: '', away: '', aet: false, pens: '', competition: '', date: '', venue: '', attendance: '', referee: '' };
  var FOOTER_PH = { home: 'Hawthorn Lane', homeScore: '3', awayScore: '1', away: 'Riverside', competition: 'Sunday Cup final', date: '2025-05-18', venue: 'Rec Ground, Hawthorn Lane', attendance: '412', referee: 'Mark Ellis', pens: '4-2' };
  function normalizeFooter(v) {
    v = v || {};
    function s(k, n) { return clean(v[k]).slice(0, n); }
    return {
      mode: 'match',
      home: s('home', FLIM.team), away: s('away', FLIM.team),
      homeScore: String(v.homeScore == null ? '' : v.homeScore).replace(/\D/g, '').slice(0, 2),
      awayScore: String(v.awayScore == null ? '' : v.awayScore).replace(/\D/g, '').slice(0, 2),
      aet: !!v.aet, pens: String(v.pens || '').replace(/[^\d\-– ]/g, '').replace(/\s*[-–]\s*/, '-').trim().slice(0, FLIM.pens),
      competition: s('competition', FLIM.competition), date: s('date', 24), venue: s('venue', FLIM.venue),
      attendance: String(v.attendance == null ? '' : v.attendance).replace(/\D/g, '').slice(0, FLIM.attendance), referee: s('referee', FLIM.referee)
    };
  }
  var MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  function ordinal(n) { var s = ['TH', 'ST', 'ND', 'RD'], v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; }
  function fmtDate(d) {
    var m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return String(d || '').toUpperCase();
    var day = +m[3];
    return day + ordinal(day) + ' ' + MONTHS[+m[2] - 1] + ' ' + m[1];
  }
  function fmtNum(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  // text with superscript ordinals (16TH). Returns {svg(x, base), w}
  function richLine(t, size, weight, track) {
    var parts = [], re = /(\d)(ST|ND|RD|TH)\b/g, last = 0, m;
    while ((m = re.exec(t))) { parts.push([t.slice(last, m.index + 1), 0]); parts.push([m[2], 1]); last = m.index + m[0].length; }
    parts.push([t.slice(last), 0]);
    parts = parts.filter(function (p) { return p[0]; });
    var sup = size * 0.62, w = 0;
    parts.forEach(function (p) { w += measure(p[0], p[1] ? sup : size, weight, track); });
    return {
      w: w,
      svg: function (x, base, fill) {
        var inner = '', up = false;
        parts.forEach(function (p) {
          if (p[1]) { inner += '<tspan font-size="' + r1(sup) + '" dy="' + r1(-size * 0.33) + '">' + esc(p[0]) + '</tspan>'; up = true; }
          else { inner += '<tspan' + (up ? ' dy="' + r1(size * 0.33) + '"' : '') + '>' + esc(p[0]) + '</tspan>'; up = false; }
        });
        return '<text x="' + r1(x) + '" y="' + r1(base) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-size="' + r1(size) + '" font-weight="' + weight +
          '" letter-spacing="' + r1(track) + '" fill="' + fill + '">' + inner + '</text>';
      }
    };
  }

  function footerGroups(v, ph) {
    function val(k) { return v[k] || (ph ? FOOTER_PH[k] : ''); }
    var ghost = ph && !(v.home || v.away || v.competition);
    var g = [];
    var home = val('home'), away = val('away');
    if (home || away) {
      var hs = v.homeScore !== '' ? v.homeScore : (ph && ghost ? FOOTER_PH.homeScore : ''), as = v.awayScore !== '' ? v.awayScore : (ph && ghost ? FOOTER_PH.awayScore : '');
      var score = hs !== '' && as !== '' ? hs + ' - ' + as : 'V';
      g.push({ type: 'score', lines: [(home + ' ' + score + ' ' + away).trim().toUpperCase()] });
      var extra = [];
      if (v.aet) extra.push('AFTER EXTRA TIME');
      if (v.pens) extra.push('PENS: ' + v.pens);
      if (extra.length) g.push({ type: 'aet', lines: extra });
    }
    var l2 = [];
    if (val('competition')) l2.push(val('competition').toUpperCase());
    if (val('date')) l2.push(fmtDate(val('date')));
    if (l2.length) g.push({ type: 'comp', lines: l2 });
    var l3 = [];
    if (val('venue')) l3.push(val('venue').toUpperCase());
    if (v.attendance) l3.push('ATTENDANCE: ' + fmtNum(v.attendance)); else if (ghost) l3.push('ATTENDANCE: ' + FOOTER_PH.attendance);
    if (val('referee')) l3.push('REFEREE: ' + val('referee').toUpperCase());
    if (l3.length) g.push({ type: 'facts', lines: l3 });
    return { groups: g, ghost: ghost };
  }
  // sizes, line pitch and the gap before each group (baseline to baseline), 900-wide print units
  var FT = {
    score: { size: 14, weight: 700, pitch: 16.4, before: 0 },
    aet: { size: 8, weight: 700, pitch: 9.6, before: 12 },
    comp: { size: 10.8, weight: 700, pitch: 12.95, before: 21 },
    facts: { size: 8.3, weight: 400, pitch: 10.4, before: 19.1 }
  };

  function renderMatchFooter(value, opts) {
    opts = opts || {};
    var v = normalizeFooter(value), S = resolveStyle(opts.style), ph = !!opts.placeholder;
    var X = +opts.x || 0, Y = +opts.y || 0, W = +opts.width || 300, H = +opts.height || 164, k = W / 300;
    var built = footerGroups(v, ph), groups = built.groups;
    if (!groups.length) return '';
    var lay;
    for (var s = 1; s >= 0.6; s -= 0.04) {
      var rows = [], y = 0, ok = true, prevType = null;
      groups.forEach(function (g, gi) {
        var t = FT[g.type], size = t.size * k * s;
        g.lines.forEach(function (line, li) {
          var rl = richLine(line, size, t.weight, 0);
          if (rl.w > W) ok = false;
          if (gi === 0 && li === 0) y = size * 0.7;            // first baseline: cap height from the top
          else if (li === 0) y += (prevType === 'score' && g.type === 'comp' ? FT.comp.before : t.before) * k * s;
          else y += t.pitch * k * s;
          rows.push({ rl: rl, base: y, size: size });
        });
        prevType = g.type;
      });
      lay = { rows: rows, h: y };
      if (ok && y <= H) break;
    }
    var top = Y + (H - lay.h) / 2, cx = X + W / 2, fill = built.ghost ? S.soft : S.ink, op = built.ghost ? ' opacity=".55"' : '';
    var svg = lay.rows.map(function (r) {
      var w = r.rl.w, sc = w > W ? W / w : 1, t = r.rl.svg(cx, top + r.base, fill);
      return sc < 1 ? '<g transform="translate(' + r1(cx) + ' 0) scale(' + r1(sc) + ' 1) translate(' + r1(-cx) + ' 0)">' + t + '</g>' : t;
    }).join('');
    return '<g class="yxd-footer" data-mode="match"' + op + '>' + svg + '</g>';
  }

  function mountFooterEditor(el, opts) {
    opts = opts || {};
    var state = normalizeFooter(opts.value || FOOTER_DEFAULT);
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var id = 'yxdf' + (++uid), dead = false;
    function emit() { if (!dead) onChange(clone(state)); }
    function field(k, label, max, extra) {
      extra = extra || {};
      return '<div class="yxd-field"><div class="yxd-row"><label class="yxd-label" for="' + id + k + '">' + label + (extra.opt ? ' <span class="yxd-opt">(optional)</span>' : '') + '</label>' +
        (max ? '<span class="yxd-count" data-c="' + k + '">' + state[k].length + '/' + max + '</span>' : '') + '</div>' +
        '<input class="yxd-input" id="' + id + k + '" data-f="' + k + '" ' + (max ? 'maxlength="' + max + '" ' : '') + (extra.type ? 'type="' + extra.type + '" ' : '') + (extra.mode ? 'inputmode="' + extra.mode + '" ' : '') +
        'autocomplete="off" placeholder="e.g. ' + esc(FOOTER_PH[k]) + '" value="' + esc(state[k]) + '"></div>';
    }
    function render() {
      el.innerHTML = '<div class="yxd yxd-f">' +
        '<div class="yxd-field"><span class="yxd-label">Score</span><div class="yxd-score">' +
        '<input class="yxd-input" data-f="home" maxlength="' + FLIM.team + '" placeholder="Home team" aria-label="Home team" value="' + esc(state.home) + '">' +
        '<input class="yxd-input yxd-goalsin" data-f="homeScore" inputmode="numeric" maxlength="2" placeholder="0" aria-label="Home score" value="' + esc(state.homeScore) + '">' +
        '<input class="yxd-input" data-f="away" maxlength="' + FLIM.team + '" placeholder="Away team" aria-label="Away team" value="' + esc(state.away) + '">' +
        '<input class="yxd-input yxd-goalsin" data-f="awayScore" inputmode="numeric" maxlength="2" placeholder="0" aria-label="Away score" value="' + esc(state.awayScore) + '">' +
        '</div><p class="yxd-hint">Prints as HAWTHORN 3 - 1 RIVERSIDE, bold, on one line.</p></div>' +
        '<div class="yxd-toggle"><span id="' + id + 'aet">After extra time</span><button type="button" class="yxd-switch" role="switch" aria-labelledby="' + id + 'aet" aria-checked="' + state.aet + '" data-sw="aet"></button></div>' +
        field('pens', 'Penalties', 0, { opt: 1 }) +
        field('competition', 'Competition', FLIM.competition) +
        '<div class="yxd-field"><label class="yxd-label" for="' + id + 'date">Date</label><input class="yxd-input" id="' + id + 'date" type="date" data-f="date" value="' + esc(/^\d{4}-\d{2}-\d{2}$/.test(state.date) ? state.date : '') + '"></div>' +
        field('venue', 'Venue', FLIM.venue, { opt: 1 }) +
        '<div class="yxd-two">' + field('attendance', 'Attendance', 0, { opt: 1, mode: 'numeric' }) + field('referee', 'Referee', FLIM.referee, { opt: 1 }) + '</div>' +
        '</div>';
    }
    function onInput(e) {
      var t = e.target, f = t.dataset && t.dataset.f; if (!f) return;
      var raw = t.value;
      if (f === 'homeScore' || f === 'awayScore' || f === 'attendance') { var d = raw.replace(/\D/g, ''); if (d !== raw) t.value = d; raw = d; }
      var next = normalizeFooter(Object.assign({}, state, (function () { var o = {}; o[f] = raw; return o; })()));
      state = next;
      var c = el.querySelector('[data-c="' + f + '"]'); if (c) c.textContent = String(state[f]).length + '/' + FLIM[f === 'home' || f === 'away' ? 'team' : f];
      emit();
    }
    function onClick(e) {
      var b = e.target.closest && e.target.closest('[data-sw]'); if (!b || !el.contains(b)) return;
      state[b.dataset.sw] = !state[b.dataset.sw]; b.setAttribute('aria-checked', state[b.dataset.sw]); emit();
    }
    el.addEventListener('input', onInput); el.addEventListener('click', onClick);
    render();
    return {
      update: function (value) { var n = normalizeFooter(value); if (JSON.stringify(n) === JSON.stringify(state)) return; state = n; render(); },
      getValue: function () { return clone(state); },
      destroy: function () { dead = true; el.removeEventListener('input', onInput); el.removeEventListener('click', onClick); el.innerHTML = ''; }
    };
  }

  /* combined footer editor: None / Message / Achievement (footer.js) + Match (this file).
     value = footer.js value (mode may be 'match') + {match: footer value} */
  function mountFooterCombined(el, opts) {
    opts = opts || {};
    var value = clone(opts.value || { mode: 'message' });
    value.match = normalizeFooter(value.match);
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var id = 'yxdc' + (++uid), sub = null, subKind = null;
    el.innerHTML = '<div class="yxd yxd-comb"><span class="yxd-label" id="' + id + '">Bottom right of your print</span>' +
      '<div class="yxd-seg4" role="radiogroup" aria-labelledby="' + id + '">' +
      [['none', 'None'], ['message', 'Message'], ['achievement', 'Achievement'], ['match', 'Match']].map(function (o) {
        return '<button type="button" role="radio" data-mode="' + o[0] + '">' + o[1] + '</button>';
      }).join('') + '</div><div class="yxd-host"></div></div>';
    var host = el.querySelector('.yxd-host');
    function emit() { onChange(clone(value)); }
    function paint() {
      [].forEach.call(el.querySelectorAll('[data-mode]'), function (b) { b.setAttribute('aria-checked', b.dataset.mode === value.mode); });
      var kind = value.mode === 'match' ? 'match' : 'footer';
      if (kind !== subKind) {
        if (sub) sub.destroy();
        host.innerHTML = '';
        var box = document.createElement('div'); host.appendChild(box);
        if (kind === 'match') sub = mountFooterEditor(box, { value: value.match, onChange: function (m) { value.match = m; emit(); } });
        else if (root.YXIFooter) {
          box.className = 'yxd-hostfooter';
          var fv = clone(value); delete fv.match;
          sub = root.YXIFooter.mount(box, { value: fv, onChange: function (f) { var m = value.match; value = f; value.match = m; value.mode = f.mode; emit(); } });
        } else sub = { destroy: function () {}, update: function () {} };
        subKind = kind;
      } else if (kind === 'footer' && sub && sub.update) { var fv2 = clone(value); delete fv2.match; sub.update(fv2); }
    }
    function onClick(e) {
      var b = e.target.closest && e.target.closest('[data-mode]'); if (!b || !el.contains(b) || b.closest('.yxd-host')) return;
      if (value.mode === b.dataset.mode) return;
      value.mode = b.dataset.mode; paint(); emit();
    }
    el.addEventListener('click', onClick);
    paint();
    return {
      update: function (v) { value = clone(v); value.match = normalizeFooter(value.match); paint(); },
      getValue: function () { return clone(value); },
      destroy: function () { if (sub) sub.destroy(); el.removeEventListener('click', onClick); el.innerHTML = ''; }
    };
  }
  function renderFooter(value, opts) {
    value = value || {};
    if (value.mode === 'match') return renderMatchFooter(value.match || value, opts);
    return root.YXIFooter ? root.YXIFooter.renderSVG(value, opts) : '';
  }

  var api = {
    mountPlayerEditor: mountPlayerEditor, renderPlayerAnnotations: renderPlayerAnnotations, layoutPlayerAnnotations: layoutPlayerAnnotations,
    normalizePlayer: normalizePlayer, isEmptyPlayer: isEmptyPlayer, captainSuffix: captainSuffix, subMarkSVG: subMarkSVG, subMarkWidth: subMarkWidth,
    mountFooterEditor: mountFooterEditor, renderMatchFooter: renderMatchFooter, mountFooterCombined: mountFooterCombined, renderFooter: renderFooter,
    normalizeFooter: normalizeFooter, formatDate: fmtDate, formatMinute: fmtMin,
    COLOURS: { subOn: GREEN, subOff: DOWN, yellow: YELLOW, red: RED, star: GOLD }, LIMITS: FLIM, PLACEHOLDERS: FOOTER_PH
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.YXIMatch = api;
})(this);
